import { once } from 'lodash';

import { SignupType } from '@zapier/auth';

import { SIGNUP_CONVERSION_EVENT, SIGNUP_INTENT_EVENT } from './constants';

import { TrackingFunction, TrackingFunctions, TrackingMetadata } from './types';

/**
 * Creates a collection of essential functions to be used across Zapier
 *
 * Takes a TrackingFunction as an argument and returns additional functions
 * built on top of that function.
 *
 * Example usage:
 *
 * const trackingFunction = (eventType, metaData) => { ... }
 *
 * export const { trackSignupConversion } = createTrackingFunctions(trackingFunction);
 *
 * trackSignupConversion(SignupType.oneTap)
 */
const createTrackingFunctions = (
  trackEvent: TrackingFunction
): TrackingFunctions => {
  const trackSignupConversion = (
    signupType: SignupType,
    metaData?: TrackingMetadata
  ) =>
    trackEvent(SIGNUP_CONVERSION_EVENT, {
      ...metaData,
      eventCategory: 'account',
      eventAction: 'created',
      eventValue: 1,
      signupType,
    });

  const trackSignupIntent = once(
    (signupType: SignupType, metaData?: TrackingMetadata) =>
      trackEvent(SIGNUP_INTENT_EVENT, {
        ...metaData,
        eventCategory: 'account',
        eventAction: 'created',
        eventValue: 1,
        signupType,
      })
  );

  const trackBirthEvent = once(
    (test, lane, extraEventData?: TrackingMetadata) => {
      const metaData = {
        ...extraEventData,
        lane,
        test,
      };
      return trackEvent(`test: ${test}_birth`, metaData);
    }
  );

  return {
    trackEvent,
    trackSignupIntent,
    trackSignupConversion,
    trackBirthEvent,
  };
};

export default createTrackingFunctions;
